import { useCallback, useSyncExternalStore } from 'react';
import type { UseWeatherResult } from '../../config/contracts';
import { usePolling } from '../poll';
import {
  WEATHER_POLL_SECONDS,
  getWeatherSnapshot,
  refreshWeather,
  subscribeWeather,
} from './weatherStore';

/**
 * ============================================================================
 * The shared weather hook. Signature is fixed by `UseWeatherResult` in
 * src/config/contracts.ts (Mayor-owned); only the body is ours.
 *
 * Mounted simultaneously by `WeatherPanel` and the Mayor's `TopBar`. All five
 * binding requirements from the Phase 0 stub are met here, and each one is
 * met in `weatherStore.ts` rather than in this file, which is the point:
 *
 *  1. SINGLE SHARED STORE — this hook holds no state. It reads a module-level
 *     snapshot through `useSyncExternalStore`, exactly like `useLocalDateKey`
 *     in src/lib/storage.ts. N mounts observe one object.
 *
 *     `usePolling` is per-mount and therefore DOES run one timer per mount,
 *     which no hook-level trick can prevent. What matters is requests, not
 *     timers: `refreshWeather()` holds a module-level in-flight promise and a
 *     30s collapse window, so two loops firing milliseconds apart produce one
 *     network round-trip. That also absorbs StrictMode's double-mount, which
 *     a `useEffect` cleanup alone cannot.
 *
 *  2. NEVER THROWS, and reports `unconfigured` rather than dying when no key
 *     is set. Nothing in this call path can raise: `fetchJson` never rejects
 *     and every normalizer returns null instead of throwing.
 *
 *  3. `forecast` and `alerts` are ALWAYS arrays — the store's only two array
 *     fields are seeded with frozen empties and never assigned null.
 *
 *  4. `refresh` is `useCallback`-stable with an empty dep list, safe to hand
 *     to a memoised child.
 *
 *  5. Polling pauses on a hidden tab, via `usePolling`'s default.
 *
 * NOTE ON `unconfigured`: with no API key we do NOT show an empty card. We
 * fetch real conditions from open-meteo (keyless) and set
 * `usingKeylessFallback: true` so the panel can show an "add a key" badge —
 * the documented deviation in DECISIONS.md. `status` is only 'unconfigured'
 * in the genuinely dead case: no key AND no data to show.
 * ============================================================================
 */
export function useWeather(): UseWeatherResult {
  const snapshot = useSyncExternalStore(subscribeWeather, getWeatherSnapshot, getWeatherSnapshot);

  // Never `disabled`: open-meteo needs no key, so there is always a source.
  usePolling(() => refreshWeather(false), WEATHER_POLL_SECONDS);

  const refresh = useCallback(() => {
    void refreshWeather(true);
  }, []);

  return { ...snapshot, refresh };
}
